<script lang="ts">
  import { PUBLIC_API_URL } from '$env/static/public';

  import { afterNavigate, goto, invalidateAll } from '$app/navigation';
  import { page } from '$app/stores';

  import { onMount } from 'svelte';

  import { get } from 'svelte/store';

  import { ClientOnly } from '@shared/ui/client-only';
  import { Container } from '@shared/ui/container';
  import { GridToggler } from '@shared/ui/grid-toggler';
  import { checkTitleComplexity, Heading } from '@shared/ui/heading';
  import { cn, parseSlug, serializeSearchParams } from '@shared/utils';

  import type { Configurations } from '@entities/configurations';

  import { ArtworksFilter } from '@features/artwork/artworks-filter';
  import { ArtworksSort } from '@features/artwork/artworks-sort';
  import { Pagination } from '@features/pagination';

  import { ArtworkList } from '@widgets/artwork-list';
  import { ArtworksFilterSidebar, filtersLibs } from '@widgets/artworks-filter-sidebar';

  import { ARTWORKS_PER_PAGE } from '../configs';
  import type { ArtAssetsList } from '../types';
  import ArtworksIntro from './artworks-intro.svelte';

  export let configurations: Configurations;
  export let artworks: ArtAssetsList;
  export let pageNumber: number;
  export let loadMore: number;
  export let totalCount: number;

  const limit = ARTWORKS_PER_PAGE;
  const defaultParams = {
    column: 'year',
    order: 'desc',
  };

  let loading = false;
  let isMounted = false;
  let container: HTMLElement;

  let prevPage = pageNumber;

  let columnField = $page.url.searchParams.get('column') || defaultParams.column;
  let sortOrder = $page.url.searchParams.get('order') || defaultParams.order;

  $: isTitleComplex = checkTitleComplexity(configurations.name);
  $: title = `Artworks of <span class="${cn('text-balance', { 'md:text-nowrap': !isTitleComplex })}">${configurations.name}${pageNumber > 1 ? ` - Page ${pageNumber}` : ''}</span>`;

  async function requestRedirect(params: { page: number; loadMore?: number; invalidateAll?: boolean }) {
    const { url, params: pageParams } = get(page);

    const newPage = params?.page || 1;
    const isSamePage = params.page === prevPage;

    if (!params.loadMore) {
      url.searchParams.delete('loadMore');
    }

    const searchParamsString = serializeSearchParams(url.searchParams);

    if (isSamePage) {
      const basePath = url.pathname;

      await goto(searchParamsString || basePath, {
        noScroll: isSamePage,
        replaceState: true,
        invalidateAll: true,
      });

      if (!searchParamsString) {
        await invalidateAll();
      }
    } else {
      const filters = filtersLibs.getFiltersFromSlug(pageParams.slug);

      const newUrl = `${newPage !== 1 ? `/artworks/${filters}page-${newPage}` : `/artworks/${filters}`}${searchParamsString}`;

      prevPage = pageNumber;

      await goto(newUrl, { invalidateAll: params.invalidateAll ?? false, noScroll: true });
    }
  }

  async function handleLoadMore(params: { page: number; loadMore?: number }) {
    const { url } = get(page);

    url.searchParams.set('loadMore', `${params.loadMore}`);

    const searchParams = configureSearchParams(params);

    const someReceivedArtworks = await handleLoadArtworks(searchParams);

    artworks = [...artworks, ...someReceivedArtworks];

    const searchParamsString = serializeSearchParams(url.searchParams);

    await goto(searchParamsString, {
      noScroll: true,
      replaceState: true,
      invalidateAll: false,
      keepFocus: true,
    });
  }

  async function handleLoadArtworks(params: URLSearchParams) {
    const searchParams = params.toString();

    try {
      const resp: any = await fetch(`${PUBLIC_API_URL}/art-assets?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { art_assets } = await resp.json();

      return art_assets;
    } catch (error) {
      console.error('Error fetching artworks', error);

      return [];
    }
  }

  function configureSearchParams(params: { page: number; loadMore?: number }) {
    const { params: pageParams } = get(page);
    const parsedParams = parseSlug(pageParams.slug);

    const preparedParams = {
      offset: `${limit * (params.page + (params.loadMore ?? 0) - 1)}`,
      limit: `${limit}`,
      column: columnField,
      order: sortOrder,
      institutions: String(configurations.id),
      ...parsedParams,
    };

    const searchParams = new URLSearchParams(preparedParams);

    return searchParams;
  }

  const applySort = async (column: string, order: string) => {
    if (!isMounted || !column || !order) return;

    const { url } = get(page);

    loading = true;
    loadMore = 0;

    url.searchParams.set('order', order);
    url.searchParams.set('column', column);

    await requestRedirect({ page: 1, loadMore, invalidateAll: true });

    loading = false;
  };

  $: applySort(columnField, sortOrder);

  onMount(() => {
    // @TODO - if svelte lifecycle methods updates in future releases and not triggering reactive variable on mount - remove this flag
    isMounted = true;
  });

  afterNavigate(({ to }) => {
    if (to?.url.searchParams?.size === 0) {
      // if we go by link to the same page with no query params - we need to reset sort params
      columnField = defaultParams.column;
      sortOrder = defaultParams.order;
    }
  });
</script>

<ClientOnly>
  <ArtworksFilterSidebar />
</ClientOnly>

<div bind:this="{container}" class="wrapper-container">
  <Heading
    {title}
    tag="h1"
    dividePosition="none"
    counter="{totalCount}"
    titleClass="{cn('inline text-balance lg:text-4xl xl:text-5xl', { 'break-all': isTitleComplex })}"
    wrapperClass="flex flex-col items-start gap-8 lg:items-end lg:flex-row normal-case"
  >
    <div class="lg:hidden">
      <ArtworksIntro />
    </div>

    <div class="flex items-center gap-9 lg:self-end">
      <ArtworksFilter />

      <div class="w-[168px]">
        <ArtworksSort bind:field="{columnField}" bind:order="{sortOrder}" />
      </div>

      <GridToggler />
    </div>
  </Heading>
</div>

<Container containerClass="lg:grid lg:grid-cols-12 lg:gap-12 hidden wrapper-container">
  <div class="lg:col-span-7">
    <ArtworksIntro />
  </div>
</Container>

<Container containerClass="py-8 md:py-12">
  <Pagination
    isFullWidth
    containerToScroll="{container}"
    pageSize="{limit}"
    requestFunction="{requestRedirect}"
    loadMoreFunction="{handleLoadMore}"
    bind:currentPage="{pageNumber}"
    bind:loadMore
    bind:totalDataCount="{totalCount}"
    bind:loading
  >
    <ArtworkList {artworks} />
  </Pagination>
</Container>
